import React from "react";
import Image from "next/image";
import Link from "next/link";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { useTranslations } from "next-intl";

type Props = {
  project: Post & {
    acf?: {
      project_feature_1_number?: string;
      project_feature_1_text?: string;
      project_feature_2_number?: string;
      project_feature_2_text?: string;
    };
  };
  locale: string;
};

const ProjectCard = ({ project, locale }: Props) => {
  const featuredMedia = project._embedded?.["wp:featuredmedia"]?.[0];
  const featuredImageSizes = featuredMedia?.["media_details"]?.sizes;
  const categories = project._embedded?.["wp:term"]?.[0];

  const hasFeatures =
    project?.acf?.project_feature_1_number ||
    project?.acf?.project_feature_1_text ||
    project?.acf?.project_feature_2_number ||
    project?.acf?.project_feature_2_text;

  const t = useTranslations("ProjectCard");

  return (
    <article className="group overflow-hidden rounded-2xl bg-white shadow-md transition-all duration-300 hover:shadow-2xl hover:shadow-slate-300/40 dark:bg-slate-800 dark:shadow-slate-900/30 dark:hover:shadow-slate-900/60">
      {/* Image Section */}
      <figure className="relative overflow-hidden">
        <Link
          href={`/projects/${project.slug}`}
          className="block"
        >
          {/* Image */}
          {featuredMedia && featuredImageSizes && (
            <div className="aspect-[4/3] overflow-hidden">
              <Image
                src={featuredImageSizes.medium_large["source_url"]}
                alt={featuredMedia.alt_text}
                width={featuredImageSizes.medium_large.width}
                height={featuredImageSizes.medium_large.height}
                className="h-full w-full object-cover transition-transform duration-700 will-change-transform group-hover:scale-105"
              />
            </div>
          )}

          {/* Gradient overlay */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/20 via-50% to-transparent" />
        </Link>

        {/* Category badges — glassmorphism */}
        {categories && categories[0].id !== 1 && (
          <div className="absolute left-0 top-0 z-[1] flex flex-wrap gap-2 p-5">
            {categories.map((category) =>
              category.id !== 1 ? (
                <span
                  className="rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white backdrop-blur-md"
                  key={category.id}
                >
                  {category.name}
                </span>
              ) : null,
            )}
          </div>
        )}

        {/* Title overlay on image */}
        <div className="absolute bottom-0 left-0 right-0 z-[1] p-6">
          <h2 className="mb-0 text-xl font-bold leading-snug text-white drop-shadow-lg lg:text-2xl">
            <Link
              href={`/projects/${project.slug}`}
              dangerouslySetInnerHTML={{ __html: project?.title?.rendered }}
              className="transition-colors hover:text-primary-foreground"
            />
          </h2>
        </div>
      </figure>

      {/* Content Section */}
      <div className="p-6 lg:p-8">
        {/* Feature Stats */}
        {hasFeatures && (
          <div className="mb-5 flex items-stretch divide-x divide-slate-200 dark:divide-slate-700">
            {(project?.acf?.project_feature_1_number ||
              project?.acf?.project_feature_1_text) && (
                <div className="flex-1 pr-4 text-center">
                  {project?.acf?.project_feature_1_number && (
                    <span className="block text-2xl font-bold text-primary lg:text-3xl">
                      {project.acf.project_feature_1_number}
                    </span>
                  )}
                  {project?.acf?.project_feature_1_text && (
                    <span className="mt-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {project.acf.project_feature_1_text}
                    </span>
                  )}
                </div>
              )}
            {(project?.acf?.project_feature_2_number ||
              project?.acf?.project_feature_2_text) && (
                <div className="flex-1 pl-4 text-center">
                  {project?.acf?.project_feature_2_number && (
                    <span className="block text-2xl font-bold text-primary lg:text-3xl">
                      {project.acf.project_feature_2_number}
                    </span>
                  )}
                  {project?.acf?.project_feature_2_text && (
                    <span className="mt-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {project.acf.project_feature_2_text}
                    </span>
                  )}
                </div>
              )}
          </div>
        )}

        {/* Excerpt */}
        {project?.excerpt?.rendered && (
          <div
            className="line-clamp-3 text-sm leading-relaxed text-muted-foreground [&>p]:mb-0"
            dangerouslySetInnerHTML={{ __html: project?.excerpt?.rendered }}
          />
        )}

        {/* CTA Link */}
        <Link
          href={`/projects/${project.slug}`}
          className="group/link mt-6 inline-flex items-center gap-2 text-sm font-bold text-primary transition-colors hover:text-primary/80"
        >
          {t("viewProject")}
          <ChevronRightIcon
            width={18}
            height={18}
            className="transition-transform duration-200 group-hover/link:translate-x-1"
          />
        </Link>
      </div>
    </article>
  );
};

export default ProjectCard;
